import React, { useContext, useEffect, useState, useCallback } from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import Fullscreen from '@material-ui/icons/Fullscreen';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import ListIcon from '@material-ui/icons/List';
import Chip from '@material-ui/core/Chip';
import CheckCircle from '@material-ui/icons/CheckCircleOutline';
import HighlightOff from '@material-ui/icons/HighlightOff';
import AttachmentIcon from '@material-ui/icons/Attachment';
import { useDropzone } from 'react-dropzone';

import moment from 'moment';

import CommentCard from '../components/CommentCard';
import TeamMemberCard from '../components/TeamMemberCard';
import Colors from '../utils/colors';
import { formatDateNoTimezone } from '../utils/dateUtils';
import apiFetch, { getData } from '../utils/apiUtils';
import { DropdownContext, ErrorContext, MilestoneStatus, PermissionsContext, NotificationContext, NOTIFICATION_TYPE } from '../utils/constants';
import EnhancedTextInput from '../components/EnhancedTextInput';
import EnhancedTable from '../components/EnhancedTable';
import { canEditInitiative, isOwnerOrEditor, isTo } from "../utils/initiatives";
import AddUserCard from "../components/AddUserCard";
import InformationBox from '../components/InformationBox';

import { stakeholderMilestoneContent } from '../components/milestone/StakeholderMilestones';
import { formatCurrency } from '../utils/currencyFormater';
import PhaseCard from "../components/initiative/PhaseCard";
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  main: {
    backgroundColor: 'white'
  },
  h1: {
    marginBottom: '0px'
  },
  columnLayout: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  overviewColumn: {
    width: '70%',
    paddingRight: '2rem',
  },
  detailsColumn: {
    width: '28%',
  },
  inputStyle: {
    width: '100%',
    border: '1px solid #ced4da',
    padding: '10px 12px',
    fontSize: '12px',
    transition: 'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderRadius: '4px',
    backgroundColor: 'white'
  },
  kpiAreaContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  kpiBox: {
    height: '144px',
    width: '49%',
    backgroundColor: Colors.lightBlue,
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer'
  },
  kpiBoxFullWidth: {
    width: '100%',
    cursor: 'pointer'
  },
  kpiBoxDark: {
    backgroundColor: Colors.darkBlue,
    color: 'white',
    fontWeight: 'bold',
    display: 'block'
  },
  commentsHeader: {
    backgroundColor: Colors.darkBlue,
    color: 'white',
    fontWeight: '700',
    marginTop: '1rem',
    marginBottom: '1rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '1rem',
  },
  attachmentsHeader: {
    backgroundColor: Colors.darkBlue,
    color: 'white',
    fontWeight: '700',
    marginTop: '1rem',
    marginBottom: '0rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '1rem',
  },
  uploadAttachmentBtn: {
    margin: '-7px 5px',
    cursor: 'pointer'
  },
  commentsDiv: {
    maxHeight: '250px',
    minHeight: '70px',
    overflowY: 'auto',
    width: '100%',
  },
  attachmentsDiv: {
    overflowY: 'auto',
    width: '100%',
  },
  newCommentDiv: {
    backgroundColor: Colors.lightBlue,
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center',
    paddingLeft: '15px',
    paddingTop: '5px'
  },
  newFileDiv: {
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center'
  },
  newFileDivBordered: {
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center',
    border: `2px dashed ${Colors.darkBlue}`
  },
  newCommentInputStyle: {
    marginLeft: '4rem',
    width: '100%',
    border: '1px solid #ced4da',
    padding: '5px 12px',
    fontSize: '12px',
    transition: 'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderRadius: '4px',
    backgroundColor: 'white'
  },
  commentInput: {
    padding: '5px 8px'
  },
  commentBtn: {
    backgroundColor: Colors.darkBlue,
    margin: '1rem',
    height: '26px',
    padding: '0'
  },
  attachBtn: {
    backgroundColor: Colors.darkBlue,
    marginLeft: '1rem',
    height: '20px',
    padding: '0',
    alignItems: 'right'
  },
  headerText: {
    fontSize: '12px'
  },
  initiativeText: {
    fontSize: '12px',
    textAlign: 'right'
  },
  settingsButtonDiv: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'flex-end',
  },
  settingsBtn: {
    cursor: 'pointer'
  },
  tagsDiv: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  flagBtn: {
    width: '100%',
    backgroundColor: Colors.darkBlue,
    "&:hover": {
      backgroundColor: Colors.brightBlue,
      color: Colors.indigoBlue
    },
    marginBottom: '1rem'
  },
  flagBtnRequested: {
    width: '100%',
    backgroundColor: Colors.secondary,
    marginBottom: '1rem'
  },
  newUserCard: {
    width: '100%',
  },
  stakeholderReviewSection: {
    marginTop: '13px',
    paddingTop: '12px',
    marginBottom: '0px',
    borderTop: '1px solid grey'
  },
  separator: {
    borderBottom: `1px solid grey`
  },
  h3: {
    marginBottom: '0px'
  },
  stakeholderReviewRow: {
    padding: '8px 0px 8px 0px'
  },
  stakeholderReviewTitle: {
    width: '70%',
    textTransform: 'uppercase'
  },
  stakeholderReviewIconAndComment: {
    width: '30%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  stakeholderReviewIcon: {
    marginRight: '20px'
  },
  checkedCircle: {
    marginRight: '1rem',
    color: Colors.tertiary,
    fontSize: 'large'
  },
  highlightOff: {
    marginRight: '1rem',
    color: '#db6a34',
    fontSize: 'large'
  },
  hiddenBtn: {
    display: 'none'
  }
}));

const useSummaryStatsStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    alignContent: 'space-between',
    height: '100%',
    fontSize: '13px',
    fontWeight: 'normal',
    padding: '0.5rem',
    textTransform: 'capitalize'
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1',
    justifyContent: 'space-between',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '2',
    justifyContent: 'space-around',
  },
  commentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    justifyContent: 'flex-end',
  },
  type: {
    fontWeight: 'bold',
    fontSize: '14px'
  },
  value: {
    alignSelf: 'center',
    fontSize: '26px',
  },
  kpiBoxIcon: {
    float: 'right'
  },
  enhanceTableClass: {
    position: 'relative',
    top: '30px'
  }
}));

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
      backgroundColor: "#E0F4FC",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 500,
      fontSize: theme.typography.pxToRem(11),
      border: "1px solid #dadde9"
  }
}))(Tooltip);

const attachmentCells = [
  { id: 'saved_filename', numeric: false, label: '' },
  { id: 'user_name', numeric: false, label: 'UPLOADED BY' },
  { id: 'filename', numeric: false, label: 'NAME' },
  { id: 'creation_date', numeric: false, label: 'UPLOADED', format: 'datetime' },
  { id: 'label', numeric: false, label: 'LABEL' },
];

export default function InitiativeCard() {
  const classes = useStyles();
  const summaryStatsClasses = useSummaryStatsStyles();
  const history = useHistory();
  const params = useParams();
  const { triggerError } = useContext(ErrorContext);
  const [tagValue, setTagValue] = useState('');
  const [commentValue, setCommentValue] = useState('');
  const { isAdmin, isWSL } = useContext(PermissionsContext);
  const notification = useContext(NotificationContext);

  const currencyFormatter = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });

  useEffect(() => {
    document.title = 'Rise by TransAlta Initiative Card';
  }, []);

  // State
  const [initiativeCard, setInitiativeCard] = useState({
    id: '',
    initiative_name: '',
    current_state: '',
    future_State: '',
    confidential: false,
    overview: '',
    workstream: '',
    status: '',
    date: '',
    costs_budgeted: '',
    benefits_budgeted: '',
    type_id: '',
    total_cost_current: ''
  });
  const [originalInitiative, setOriginalInitiative] = useState({});
  const [tags, setTags] = useState([]);
  const [initiativeTeam, setInitiativeTeam] = useState([]);
  const [validate, setValidate] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [newCardNumber, setNewCardNumber] = useState(1);
  const { dropdownValues } = useContext(DropdownContext);
  const [comments, setComments] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [summaryStats, setSummaryStats] = useState([]);

  const [kpiStats, setKpiStats] = useState([]);
  const [milestonePlanStats, setMilestonePlanStats] = useState([]);
  const [impactTrackingStats, setImpactTrackingStats] = useState([]);

  const onError = () => triggerError({
    title: 'Communication Error',
    description: 'We were unable to read your initiative data from the server. Please try again.',
    onRetry: () => window.location.reload(),
  });

  const handleInitiativeCardError = (error) => {
    if (error.status === 404) {
      history.push('/ui/errors/error404');
    } else {
      onError();
    }
  };
  useEffect(() => {
    comments.sort((a, b) => (new Date(a.creation_date)) - (new Date(b.creation_date)));
    fileList.sort((a, b) => (new Date(a.creation_date)) - (new Date(b.creation_date)));

    // scroll to bottom after timeout.
    setTimeout(() => {
      scrollToBottom('comments-scroll-div');
    }, 100);
  }, [comments, fileList]);

  const refreshInitiative = () => {
    getData('/initiatives/' + params.id, [setInitiativeCard, setOriginalInitiative], handleInitiativeCardError);
    getData('/initiatives/' + params.id + '/comments/', setComments, onError);
    getData('/initiatives/' + params.id + '/files', setFileList, onError);
    getData(`/initiatives/${params.id}/milestones`, setMilestones, onError);
  };

  useEffect(() => {
    getData('/initiatives/' + params.id, [setInitiativeCard, setOriginalInitiative], handleInitiativeCardError);
  }, [params.id]);

  useEffect(() => {
    if (originalInitiative && originalInitiative.id) {
      getData(`/initiatives/${params.id}/comments/`, setComments, onError);
      getData(`/initiatives/${params.id}/tags`, setTags, onError);
      getData(`/initiatives/${params.id}/team`, setInitiativeTeam, onError);
      getData(`/initiatives/${params.id}/kpis`, setKpis, onError);
      getData(`/initiatives/${params.id}/milestones`, setMilestones, onError);
      getData(`/initiatives/${params.id}/summary_stats`, setSummaryStats, onError);
      getData(`/initiatives/${params.id}/files`, setFileList, onError);
    }
  }, [params.id, originalInitiative]);

  useEffect(() => {
    if (summaryStats) {
      const kpiStats = summaryStats.length > 0 ? summaryStats.filter(s => s.type === 'KPI')[0] : null;
      const milestonePlanStats = summaryStats.length > 0 ? summaryStats.filter(s => s.type === 'Milestone Plan')[0] : null;
      const impactTrackingStats = summaryStats.length > 0 ? summaryStats.filter(s => s.type === 'Value Tracking')[0] : null;

      if (impactTrackingStats) {
        impactTrackingStats.value = formatCurrency(impactTrackingStats.value);
      }

      setKpiStats(kpiStats);
      setMilestonePlanStats(milestonePlanStats);
      setImpactTrackingStats(impactTrackingStats);
    }
  }, [summaryStats]);

  useEffect(() => {
    if (notification && notification.initiativeId == params.id) {
      switch (notification.type) {
        case NOTIFICATION_TYPE.TAG:
          getData(`/initiatives/${params.id}/tags`, setTags, () => { console.log('Error loading tags') });
          break;
        case NOTIFICATION_TYPE.COMMENTS:
          getData(`/initiatives/${params.id}/comments/`, setComments, () => { console.log('Error loading comments') });
          break;
        case NOTIFICATION_TYPE.ATTACHMENT:
          getData(`/initiatives/${params.id}/files`, setFileList, () => { console.log('Error loading attachments') });
          break;
        case NOTIFICATION_TYPE.APPROVAL:
          getData(`/initiatives/${params.id}/comments/`, setComments, () => { console.log('Error loading comments') });
          getData('/initiatives/' + params.id, [setInitiativeCard, setOriginalInitiative],
            () => { console.log('Error loading initiative') });
          break;
        case NOTIFICATION_TYPE.TEAM_MEMBER:
          getData(`/initiatives/${params.id}/team`, setInitiativeTeam, () => { console.log('Error loading team') });
          break;
        case NOTIFICATION_TYPE.KPI:
          getData(`/initiatives/${params.id}/kpis`, setKpis, () => { console.log('Error loading kpi') });
          getData(`/initiatives/${params.id}/summary_stats`, setSummaryStats, () => { console.log('Error loading kpi') });
          break;
        case NOTIFICATION_TYPE.MILESTONE:
          getData(`/initiatives/${params.id}/milestones`, setMilestones, () => { console.log('Error loading milestones') });
          getData(`/initiatives/${params.id}/summary_stats`, setSummaryStats, () => { console.log('Error loading summary') });
          getData('/initiatives/' + params.id, [setInitiativeCard, setOriginalInitiative],
            () => { console.log('Error loading initiative') });
          break;
        case NOTIFICATION_TYPE.VALUES:
          getData(`/initiatives/${params.id}/summary_stats`, setSummaryStats, () => { console.log('Error loading summary') });
          getData('/initiatives/' + params.id, [setInitiativeCard, setOriginalInitiative],
            () => { console.log('Error loading initiative') });
          break;
        case NOTIFICATION_TYPE.APPROVAL:
        case NOTIFICATION_TYPE.INITIATIVE:
          getData('/initiatives/' + params.id, [setInitiativeCard, setOriginalInitiative],
            () => { console.log('Error loading initiative') });
          break;
        default:
          console.log(`Could not find ${notification.type}`)
      }
    }
  }, [notification]);


  const getVisibility = () => {
    const filteredList = dropdownValues.filter(item => item.id === initiativeCard.confidential);
    if (filteredList.length > 0) {
      return filteredList[0].value
    }
    return '';
  };

  const getCompletionDate = () => {
    return formatDateNoTimezone(initiativeCard.max_date, "YYYY-MMMM-DD");
  };

  const handleTagDelete = (id) => () => {
    apiFetch('/initiatives/tags/' + id, 'DELETE', JSON.stringify({ initiative_id: params.id }), () => {
      const newTags = tags.filter(tag => tag.id !== id);
      setTags(newTags);
    }, () => triggerError({
      title: 'Deletion Error',
      description: `We were unable to delete the tag. Please try again.`,
      onRetry: handleTagDelete(id)
    }));
  };

  const handleTagAdd = (value) => {
    if (value.trim().length > 0 && (value.endsWith('\n') || value.endsWith('\r') || value.endsWith(' '))) {
      apiFetch('/initiatives/tags', 'PUT',
        JSON.stringify({ name: tagValue, initiative_id: params.id }),
        (results) => {
          const newtags = Object.assign([], tags);
          newtags.push(results[0]);
          setTags(newtags);
          setTagValue('');
        }, () => triggerError({
          title: 'Creation Error',
          description: `We were unable to create the tag "${tagValue}. Please try again.`,
          onRetry: handleTagAdd.bind(null, value)
        }));
    } else {
      setTagValue(value.trim());
    }
  };

  const handleFieldChange = (field) => (value) => {
    const newCard = Object.assign({}, initiativeCard);
    newCard[field] = value;
    setInitiativeCard(newCard);
  };

  const handleBlur = (field) => (event) => {
    event.persist();
    const value = event.target.value;
    if (!value) {
      setValidate(true);
    }
    if (originalInitiative[field] === value || !value) {
      return;
    }
    apiFetch('/initiatives/updatefield', 'PATCH',
      JSON.stringify({ id: params.id, field, value }),
      (results) => {
        setValidate(false);
        setOriginalInitiative(initiativeCard);
      },
      () => triggerError({
        title: 'Update Error',
        description: `We were unable to update field=${field} with value=${value}. Please try again.`,
        onRetry: handleFieldChange(field).bind(null, event)
      }));
  };

  const handleMemberAddClick = () => {
    history.push('/ui/initiativedetails/' + params.id);
  };

  const scrollToBottom = (id) => {
    let div = document.getElementById(id);
    if (div) {
      div.scrollTop = div.scrollHeight - div.clientHeight;
    }
  };

  const handleCommentChange = (value) => {
    if (value.length < 2000) {
      setCommentValue(value);
    }

    if (value.endsWith('\n') || value.endsWith('\r')) {
      handleCommentAdd();
    }
  };

  const handleCommentAdd = () => {
    if (commentValue.trim().length === 0) {
      return;
    }
    const oldCommentValue = commentValue.trim();
    setCommentValue('');
    apiFetch('/initiatives/addcomment', 'POST',
      JSON.stringify({ name: oldCommentValue, initiative_id: params.id }),
      (results) => {
        const newComment = Object.assign([], comments);
        newComment.push(results[0]);
        setComments(newComment);
        setCommentValue('');
        scrollToBottom('comments-scroll-div');
      }, () => {
        setCommentValue(oldCommentValue);
        scrollToBottom('comments-scroll-div');
        triggerError({
          title: 'Comment Error',
          description: `We were unable to add your comment to the initiative. Please try again.`,
          onRetry: handleCommentAdd
        });
      });
  };

  const handleFlagForAssist = () => {
    const flag_assist = !!initiativeCard.flag_assist;

    apiFetch('/initiatives/' + initiativeCard.id + '/flag-for-assistance', 'PATCH',
      JSON.stringify({ id: initiativeCard.id, flag_assist: !flag_assist }),
      refreshInitiative,
      () => triggerError({
        title: 'Flagging Error',
        description: `We were unable to flag this initiative for assistance. Please try again.`,
        onRetry: handleFlagForAssist
      }));
  };

  const handleShowChangeLog = () => {
    history.push(`/ui/change-log/${initiativeCard.id}`);
  };

  const goToPage = (route) => () => {
    history.push(route + params.id);
  };

  const disabled = () => {
    return !canEditInitiative(initiativeTeam);
  };

  const noAttachments = () => {
    return fileList.length === 0 ? true : false;
  }

  const getDropdownValue = (field) => {
    if (initiativeCard[field] === null || initiativeCard[field] === '') {
      return 'Not Set';
    }

    const filteredValues = dropdownValues.filter(item => item.id === initiativeCard[field]);

    if (filteredValues.length === 1) {
      return filteredValues[0].value;
    }
    return 'Invalid Value of ' + initiativeCard[field];
  };

  const getFlagForAssistanceBtn = () => {
    if (initiativeCard.flag_assist) {
      return (
        <Button variant="contained"
          onClick={!!isWSL ? handleFlagForAssist : null}
          className={classes.flagBtnRequested}>
          <FlagOutlinedIcon />
          ASSISTANCE REQUESTED
        </Button>
      );
    }
    return (
      <HtmlTooltip title='This button will send an automated message to your Workstream Lead notifying them you need support with your initiative'>
        <Button variant="contained"
          className={classes.flagBtn}
          color="primary"
          onClick={handleFlagForAssist}
          disabled={!isOwnerOrEditor(initiativeTeam) || !isTo(initiativeTeam)}>
          <FlagOutlinedIcon />
          FLAG FOR ASSISTANCE
        </Button>
      </HtmlTooltip>
    );
  };

  const getShowChangeLogBtn = () => {
    let isLogViewer = Boolean(false);
    if (originalInitiative.roles !== undefined) {
      originalInitiative.roles.forEach(role => {
        if (role === "Finance Approver" || role === "Sponsor" || role === "MD" || role === "WSL") {
          isLogViewer = true;
        }
      });
    }

    return isAdmin || isLogViewer ? (
      <Button variant="contained"
        className={classes.flagBtn}
        color="primary"
        onClick={handleShowChangeLog}>
        <ListIcon />
        SHOW LOG
      </Button>
    ) : null;
  };

  const handleCardAdded = () => {
    getData('/initiatives/' + params.id + '/team', setInitiativeTeam, () => triggerError({
      title: 'Communication Error',
      description: 'We were unable to read your initiative data from the server. Please try again.',
      onRetry: () => window.location.reload(),
    }));
    // This sillyness is to force a new key for the card.
    setNewCardNumber(newCardNumber + 1);
  };

  const rawHandleAttachmentUpload = (file) => {
    const maxFileSize = process.env.MAX_UPLOAD_SIZE || 1000000000;
    if (file.size > maxFileSize) {
      triggerError({
        title: 'File Too Big',
        description: `The file was larger than ${maxFileSize / 1000}MB maximum!`,
        onRetry: () => { }
      });
      return;
    }
    const data = new FormData();
    data.append('file', file);
    apiFetch('/initiatives/' + params.id + '/file', 'POST', data,
      data => {
        setFileList(data);
      },
      (error) => {
        triggerError({
          title: 'Communication Error',
          description: 'We were unable to save your attachment. Please try again.',
          onRetry: selectFiles
        });
      },
      {});
  };

  const handleAttachmentUpload = (event) => {
    const file = event.target.files[0];
    rawHandleAttachmentUpload(file);
  };

  const handleDeleteAttachment = (id) => () => {
    apiFetch(`/downloads/${params.id}/files/${id}`, 'DELETE', '', () => {
      refreshInitiative();
    }, () => triggerError({
      title: 'Deletion Error',
      description: `We were unable to delete the attachment. Please try again.`
    }));
  }

  const handleLabelChange = (id) => (event) => {
    const value = event.target.value;
    apiFetch(`/initiatives/${params.id}/files/${id}`, 'PATCH',
      JSON.stringify({ id: id, value }),
      (results) => {
        refreshInitiative();
        setOriginalInitiative(initiativeCard);
      },
      () => triggerError({
        title: 'Update Error',
        description: `We were unable to update the attachment label with value=${value}. Please try again.`,
        onRetry: handleLabelChange(id).bind(null, event)
      }));
  }

  // drag & drop implementation
  const onDrop = useCallback(acceptedFiles => {
    (acceptedFiles.length > 0) && rawHandleAttachmentUpload(acceptedFiles[0]);
  }, []);

  const { getRootProps, isDragActive } = useDropzone({ onDrop });
  const dropRootProps = disabled() ? {} : { ...getRootProps() };   // disabling drag & drop if needed

  const selectFiles = () => {
    document.getElementById('attachment-file-input').click();
  };

  const getAddUserCard = () => {
    const users = initiativeTeam.filter(item => item.role === 'Owner' || item.role === 'Editor' || item.role === 'Viewer');
    if (users.length < 6) {
      return (
        <div className={classes.newUserCard}>
          <AddUserCard
            teamMembers={initiativeTeam}
            disabled={disabled()}
            key={'user-card-new-' + newCardNumber}
            hideRemoveBtn={true}
            addTeamMethod={handleCardAdded}
          />
        </div>
      );
    }
    return '';
  };

  // JSX components
  const getSummaryStatsTile = ({ type, subType, value, desc, message }) => {
    return (
      <div className={summaryStatsClasses.container}>
        <div className={summaryStatsClasses.headerContainer}>
          <div className={summaryStatsClasses.type}>{type}</div>
          <Fullscreen className={summaryStatsClasses.kpiBoxIcon} />
        </div>

        <div className={summaryStatsClasses.titleContainer}>
          <div className={summaryStatsClasses.subType}>{subType}</div>
          <div className={summaryStatsClasses.value}>{value}</div>
        </div>

        <div className={summaryStatsClasses.commentsContainer}>
          <div>{desc}</div>
          <div>{message}</div>
        </div>
      </div>
    );
  };

  const getKPIArea = () => {
    if (!initiativeCard.build_sponsor_approval) {
      return '';
    }
    return (
      <div className={classes.kpiAreaContainer}>
        {originalInitiative.hasMilestones ?
          <div className={`${classes.kpiBox} ${classes.kpiBoxDark}`} onClick={goToPage('/ui/initiativemilestones/')}>
            {milestonePlanStats && getSummaryStatsTile(milestonePlanStats)}
          </div> :
          <div className={classes.kpiBox} onClick={goToPage('/ui/initiativemilestones/')} >
            <AddCircleOutlineIcon />
            Add a Milestone
          </div>
        }
        {originalInitiative.hasValues ?
          <div className={`${classes.kpiBox} ${classes.kpiBoxDark}`} onClick={goToPage('/ui/initiativevalues/')}>
            {impactTrackingStats && getSummaryStatsTile(impactTrackingStats)}
          </div> :
          <div className={classes.kpiBox} onClick={goToPage('/ui/initiativevalues/')} >
            <AddCircleOutlineIcon />
            Add Value Tracking
          </div>
        }
      </div>
    );
  };

  const getMilestoneIconAndComment = (milestoneType) => {
    let icon, comment = '';

    //const milestonesApprovals = milestones.filter( milestne => milestne.st );
    const milestoneApprovals = milestones.filter(function (currentMilestone) {
      return currentMilestone.sent_for_review_user !== null;
    });

    const milestone = milestoneApprovals.find(({ type }) => type === milestoneType);

    if (milestone) {
      const { reviewer_name, review_verdict } = milestone;
      comment = reviewer_name ? `Reviewed by ${milestone.reviewer_name}` : 'Not Yet Reviewed';
      icon = review_verdict ? <CheckCircle className={classes.checkedCircle} /> : comment === 'Not Yet Reviewed' ? true : <HighlightOff className={classes.highlightOff} />;
    }

    return { icon, comment };
  };

  const getStakeholderMilestone = (stakeholderMilestone) => {
    const { milestoneType, title } = stakeholderMilestone;
    const { icon, comment } = getMilestoneIconAndComment(milestoneType);

    if (!icon) {
      return '';
    }

    return (
      <div className={`${classes.columnLayout} ${classes.stakeholderReviewRow} ${classes.separator}`} key={`milestone_${milestoneType}`}>
        <div className={classes.stakeholderReviewTitle}>{title}</div>
        <div className={classes.stakeholderReviewIconAndComment}>
          <div className={classes.stakeholderReviewIcon}>{icon}</div><div>{comment}</div>
        </div>
      </div>
    );
  };

  const getStakeholderMilestoneList = () => {
    const stakeHolderMilestones = stakeholderMilestoneContent.map(getStakeholderMilestone);
    const filteredMilestones = stakeHolderMilestones.filter(stone => stone);
    if (filteredMilestones.length === 0) {
      return '';
    }

    return (
      <div className={classes.stakeholderReviewSection}>
        <h3 className={`${classes.h3} ${classes.separator}`}>Stakeholder Reviews</h3>
        {stakeHolderMilestones}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <main className={classes.main}>
        <h1 className={classes.h1}>{initiativeCard.initiative_name}</h1>
        <PhaseCard initiative={initiativeCard} onSuccess={refreshInitiative} milestones={milestones} kpis={kpis} />

        <div className={classes.columnLayout}>
          <div className={classes.overviewColumn}>
            <h3>OVERVIEW</h3>
            <EnhancedTextInput
              maxText={initiativeCard.is_historic_data ? 14000 : 255}
              validate={validate}
              changeMethod={handleFieldChange('overview')}
              initialValue={initiativeCard.overview}
              autoFocus={true}
              disabled={disabled()}
              onBlurHandler={handleBlur('overview')}
            />

            {originalInitiative && !!!originalInitiative.is_historic_data &&
              <div>
                <h3>CURRENT</h3>
                <EnhancedTextInput
                  maxText={4000}
                  validate={validate}
                  changeMethod={handleFieldChange('current_state')}
                  initialValue={initiativeCard.current_state}
                  disabled={disabled()}
                  onBlurHandler={handleBlur('current_state')}
                />
                <h3>FUTURE STATE</h3>
                <EnhancedTextInput
                  maxText={4000}
                  validate={validate}
                  changeMethod={handleFieldChange('future_state')}
                  initialValue={initiativeCard.future_state}
                  disabled={disabled()}
                  onBlurHandler={handleBlur('future_state')}
                />
                <h3>BRIDGE THE GAP</h3>
                <EnhancedTextInput
                  maxText={4000}
                  validate={validate}
                  changeMethod={handleFieldChange('bridge_gap')}
                  initialValue={initiativeCard.bridge_gap}
                  disabled={disabled()}
                  onBlurHandler={handleBlur('bridge_gap')}
                />
              </div>
            }

            <hr />
            {getKPIArea()}
            {getStakeholderMilestoneList()}

            <div className={classes.commentsHeader} >
              Comments
            </div>
            <div className={classes.commentsDiv} id="comments-scroll-div">
              {comments.map(comment => {
                return <CommentCard
                  key={comment.filename ? 'file-' + comment.id : 'comment-' + comment.id} comment={comment}
                />;
              })}
            </div>
            <div className={classes.newCommentDiv} >
              <EnhancedTextInput
                maxText={2000}
                changeMethod={handleCommentChange}
                initialValue={commentValue}
                className={classes.commentInput}
                placeholder="Start typing your message"
              />
              <Button variant="contained"
                className={classes.commentBtn}
                color="primary"
                onClick={handleCommentAdd}>
                SEND
              </Button>
            </div>


            <div className={classes.attachmentsHeader} >
              Attachments
              <Button variant="contained"
                className={classes.attachBtn}
                color="primary"
              ><AttachmentIcon
                  className={disabled() ? classes.hiddenBtn : classes.uploadAttachmentBtn}
                  onClick={selectFiles}
                />
              </Button>
            </div>

            { /* enable drag & drop for this control if user has permissions */}
            <div className={isDragActive ? classes.newFileDivBordered : classes.newFileDiv}
              {...dropRootProps}>

              <input id="attachment-file-input"
                type="file"
                name="name"
                style={{ display: 'none' }}
                onChange={handleAttachmentUpload}
              />

              <div className={classes.attachmentsDiv} id="comments-scroll-div">
                <EnhancedTable
                  id='08cadf5d-e9ce-4029-afb6-a0c2d18b0fc1'
                  headCells={attachmentCells}
                  rows={fileList}
                  labels={useContext(DropdownContext).dropdowns['AttachmentLabel']}
                  onDelete={handleDeleteAttachment}
                  onChange={handleLabelChange}
                />
              </div>
            </div>
          </div>

          <div className={classes.detailsColumn}>
            <div className={classes.columnLayout}>
              <div className={classes.columnLayout}><InformationBox text='Initiative Details' description={'Click on the sprocket icon to edit initiative details'} variant={'string'}/></div>
              <div className={classes.settingsButtonDiv}>
                <SettingsOutlinedIcon
                  onClick={handleMemberAddClick}
                  className={classes.settingsBtn} />
              </div>
            </div>
            <hr />
            <div className={classes.headerText}>WORKSTREAM</div>
            <div className={classes.initiativeText}>{initiativeCard.workstream_name}</div>
            <hr />
            <div className={classes.headerText}>COMPLETION DATE</div>
            <div className={classes.initiativeText}>{getCompletionDate()}</div>
            <hr />
            <div className={classes.headerText}>INITIATIVE VISIBILITY</div>
            <div className={classes.initiativeText}>{getVisibility()}</div>
            <hr />
            <div className={classes.headerText}>TAGGING</div>
            <div className={classes.tagsDiv}>{tags.map((tag) => {
              return <Chip label={tag.tag}
                size="small"
                onDelete={handleTagDelete(tag.id)}
                color="primary"
                key={tag.id + '-tag'}
              />;
            })}</div>
            <EnhancedTextInput
              maxText={255}
              changeMethod={handleTagAdd}
              initialValue={tagValue}
              disabled={disabled()}
              placeholder="Enter a tag..."
            />
            <hr />
            <div className={classes.headerText}>CLASSIFICATION</div>
            <div className={classes.initiativeText}>{
              getDropdownValue('classification')}
            </div>
            <hr />
            <div className={classes.headerText}>COSTS BUDGETED</div>
            <div className={classes.initiativeText}>
              {getDropdownValue('costs_budgeted')}
            </div>
            <hr />
            <div className={classes.headerText}>BENEFITS BUDGETED</div>
            <div className={classes.initiativeText}>
              {getDropdownValue('benefits_budgeted')}
            </div>
            <hr />
            <div className={classes.headerText}>INITIATIVE TYPE</div>
            <div className={classes.initiativeText}>
              {getDropdownValue('type_id')}
            </div>
            <hr />
            {<div className={classes.headerText}>TOTAL COST</div>}
            {<div className={classes.initiativeText}>{initiativeCard.total_cost_current === null ? currencyFormatter.format(0) : currencyFormatter.format(initiativeCard.total_cost_current)}</div>}
            {<hr />}
            <div className={classes.headerText}>RISK</div>
            <div className={classes.initiativeText}>
              {getDropdownValue('risk_label_id')}
            </div>
            <hr />
            {getFlagForAssistanceBtn()}
            {getShowChangeLogBtn()}
            Initiative Team
            {initiativeTeam.filter(member => member.role === 'Owner').map(owner => {
              return <TeamMemberCard teamMember={owner} key={'team-member-' + owner.initiativeUserId} />
            })}
            {initiativeTeam.filter(member => member.role === 'Editor' || member.role === 'Viewer').map(member => {
              return <TeamMemberCard teamMember={member} key={'team-member-' + member.initiativeUserId} />
            })}
            {getAddUserCard()}
          </div>
        </div>
      </main>
    </div>
  );
}
